import type { CppInvoice, CppInvoiceBand, CppInvoiceDay } from '~/services/LuxLoyaltyService.types';

import currencyFormatter, { supportedCurrencyDisplay } from '~/utils/currencyFormatter';

import { formatCppCents } from '../cppFormat';

// Re-exported so the grid, the CSV and the invoice keep importing a rate formatter from one place.
export { formatCppCents };

/** Largest rounding drift tolerated on a one-day period, in cents. */
const MIN_RECONCILIATION_TOLERANCE_CENTS = 5;

/** Rounding drift tolerated per day of the period, in cents. */
const CENTS_OF_DRIFT_PER_DAY = 2;

/** Currency used when a period carries no rate card and therefore no currency of its own. */
const FALLBACK_CURRENCY = 'AUD';

/** Invoice amounts are cents, so they always render to two decimal places. */
const CENT_FRACTION_DIGITS = 2;

/** Locale used for point formatting across the invoice and the transactions grid. */
const LOCALE = 'en-AU';

/** A subtotal with GST applied. */
export interface GstBreakdown {
  subtotalCents: number;
  gstCents: number;
  totalCents: number;
}

/**
 * Applies a partner's GST rate to a subtotal.
 *
 * @param subtotalCents - The invoice subtotal in cents (may be negative for a credit note).
 * @param gstRate - The partner's GST rate as a fraction, e.g. `0.1` for 10%.
 */
export function applyGst(subtotalCents: number, gstRate: number): GstBreakdown {
  // `Math.round` halves away from zero for negatives in JS (`Math.round(-0.5) === -0`), so a
  // credit note is rounded on its magnitude and re-signed to keep GST symmetric with the subtotal.
  const magnitude = Math.round(Math.abs(subtotalCents) * gstRate);
  const gstCents = subtotalCents < 0 ? -magnitude : magnitude;

  return { subtotalCents, gstCents, totalCents: subtotalCents + gstCents };
}

/**
 * Formats an amount in cents as invoice currency, e.g. `$1,200.00`.
 *
 * Deliberately renders the narrow symbol rather than forcing `A$`: the period's currency comes
 * from the rate cards and is not guaranteed to be AUD, so a hard-coded `A$` would mislabel a
 * partner billed in another currency. The currency is stated once alongside the figures instead —
 * see {@link currencyCode}.
 */
export function formatCurrencyCents(amountCents: number, currency: string | null): string {
  return currencyFormatter(currencyCode(currency), amountCents / 100, CENT_FRACTION_DIGITS, supportedCurrencyDisplay);
}

/** The period's ISO currency code, for column headers and the invoice's "amounts in" note. */
export function currencyCode(currency: string | null): string {
  return currency?.trim() || FALLBACK_CURRENCY;
}

/** Formats a signed point count with thousands separators. */
export function formatSignedPoints(points: number): string {
  return new Intl.NumberFormat(LOCALE).format(points);
}

/**
 * Renders the distinct CPP rate(s) a set of priced bands was charged at.
 *
 * Activity spanning a tier boundary is priced across several bands, so there is not always a single
 * rate. Distinct rates are listed in band order rather than blended into an average, which would
 * invent a rate that appears on no rate card.
 *
 * @param bands - The priced bands, from a day's `tierBreakdown` or a transaction's `bands`.
 * @returns The rate(s), e.g. `"1.2c"` or `"1.2c, 0.9c"`; empty when nothing could be priced.
 */
export function formatBandRates(bands: Array<CppInvoiceBand>): string {
  return [...new Set(bands.map((band) => band.cpp))].map(formatCppCents).join(', ');
}

/**
 * Formats a GST rate as a percentage label, e.g. `10%`, `12.5%`.
 *
 * Rounds away binary floating-point noise before rendering: `0.07 * 100` is `7.000000000000001`,
 * which a naive "does it divide evenly" check reads as fractional and renders as `7.00%`. Trailing
 * zeros are dropped so a whole percentage stays whole.
 */
export function formatGstRate(rate: number): string {
  const percent = Math.round(rate * 100 * 1000) / 1000;
  return `${percent}%`;
}

/** What a rate-group row represents. */
export type RateGroupLineKind = 'TIER' | 'REVERSAL' | 'ROUNDING';

/** One row of the aggregated (rate-group) invoice. */
export interface RateGroupLine {
  key: string;
  kind: RateGroupLineKind;
  /** Row description without the amount — that is the right-hand column, e.g. `1,240,000 points issued @ 0.55c per point`. */
  description: string;
  /** Signed points attributed to this row. */
  points: number;
  /** The CPP this row was priced at, or null for a row not priced at a single rate. */
  cpp: number | null;
  /** Signed amount in cents. */
  amountCents: number;
}

/** The aggregated invoice's rows and the subtotal they add up to. */
export interface RateGroupLines {
  lines: Array<RateGroupLine>;
  /** Always `invoice.totals.amountCents`. Equals the sum of `lines` unless `unreconciledCents` is set. */
  subtotalCents: number;
  /**
   * Zero when the rows reconcile to the subtotal (after any rounding line). Non-zero means the
   * per-day bands did not account for the period total by more than rounding can explain, which is
   * a data problem rather than a presentational one — the caller must refuse to issue the invoice.
   */
  unreconciledCents: number;
}

/** Cents of rounding drift a period of `dayCount` days can legitimately accumulate. */
function reconciliationToleranceCents(dayCount: number): number {
  // A day can round at most a cent per band, and a handful of bands per day is the realistic
  // ceiling; the floor covers a single-day period with several tiers.
  return Math.max(MIN_RECONCILIATION_TOLERANCE_CENTS, dayCount * CENTS_OF_DRIFT_PER_DAY);
}

/**
 * Whether a day contributed nothing at all to the invoice.
 *
 * A month is returned with a row for every calendar day, so a partner active on a handful of them
 * produces a page of `0 / — / $0.00` rows that bury the days actually being billed.
 *
 * Deliberately stricter than "zero cost". A day is only empty when it also moved no points, and an
 * *unpriced* day is never empty however it looks — its `amountCents` is null rather than 0, it
 * blocks the invoice, and hiding it would take a problem off the document. So a day carrying
 * activity that happens to price to nothing stays visible too.
 */
export function isEmptyInvoiceDay(day: CppInvoiceDay): boolean {
  return (
    day.amountCents === 0 &&
    day.creditPoints === 0 &&
    day.debitPoints === 0 &&
    day.refundPoints === 0 &&
    day.tierUpgradeCount === 0
  );
}

/**
 * Aggregates an invoice into one row per CPP tier that applied during the period.
 *
 * Every day's priced bands are grouped by their rate, so a partner on a flat CPP produces a single
 * row and a tiered partner produces one row per tier actually reached. Refunds carry no band
 * breakdown — the service prices them against the day they reverse — so they aggregate into their
 * own reversal row rather than being folded into a rate they were never charged at.
 *
 * The rows are guaranteed to sum to the subtotal. Band amounts are individually rounded and the
 * service documents them as non-summable, so grouping them can miss the authoritative period total
 * by a cent or two; rather than let an invoice show rows that do not add up to their own subtotal,
 * any residual becomes an explicit rounding row.
 *
 * @param invoice - The invoice as returned by `POST /cost-per-point/invoice`.
 * @returns The ordered rows, highest rate first, and the subtotal.
 */
export function buildRateGroupLines(invoice: CppInvoice): RateGroupLines {
  const byCpp = new Map<number, { points: number; amountCents: number }>();
  let reversalPoints = 0;
  let reversalAmountCents = 0;

  for (const day of invoice.days) {
    for (const band of day.tierBreakdown) {
      const existing = byCpp.get(band.cpp) ?? { points: 0, amountCents: 0 };
      byCpp.set(band.cpp, {
        points: existing.points + band.pointsInBand,
        amountCents: existing.amountCents + band.amountCents,
      });
    }

    for (const refund of day.refunds) {
      // `points` is a magnitude; a reversal always reduces what is billed, hence the negation.
      reversalPoints -= refund.points;
      // Null when the origin could not be priced — that degrades to zero money here and is
      // reported through the invoice's `UNPRICED_REFUND_ORIGIN` blocker instead.
      reversalAmountCents += refund.amountCents ?? 0;
    }
  }

  const lines: Array<RateGroupLine> = [...byCpp.entries()]
    .sort(([a], [b]) => b - a)
    .map(([cpp, totals]) => ({
      key: `cpp-${cpp}`,
      kind: 'TIER' as const,
      description: `${formatSignedPoints(totals.points)} points issued @ ${formatCppCents(cpp)} per point`,
      points: totals.points,
      cpp,
      amountCents: totals.amountCents,
    }));

  if (reversalPoints !== 0 || reversalAmountCents !== 0) {
    lines.push({
      key: 'reversals',
      kind: 'REVERSAL',
      description: `${formatSignedPoints(reversalPoints)} points reversed`,
      points: reversalPoints,
      cpp: null,
      amountCents: reversalAmountCents,
    });
  }

  const subtotalCents = invoice.totals.amountCents;
  const residual = subtotalCents - lines.reduce((sum, line) => sum + line.amountCents, 0);

  // Each day's bands are rounded independently, so the grouped rows can miss the authoritative
  // total by a cent or so per day. Anything beyond that is not rounding — it means the days did not
  // account for the period total — and must not be dressed up as a tidy-up line on a tax invoice.
  const tolerance = reconciliationToleranceCents(invoice.days.length);
  const isRounding = Math.abs(residual) <= tolerance;

  if (residual !== 0 && isRounding) {
    lines.push({
      key: 'rounding',
      kind: 'ROUNDING',
      description: 'Rounding adjustment',
      points: 0,
      cpp: null,
      amountCents: residual,
    });
  }

  return { lines, subtotalCents, unreconciledCents: isRounding ? 0 : residual };
}
